/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-fragments */
import { createFilter } from "react-select";
import { gatherConditionalProps, isFunction } from "./utils";

const debounce = require("debounce-promise");

export const COMMON_PROPS = [
  "name",
  "autoFocus",
  "inputValue",
  "filterOption",
  "isClearable",
  "isDisabled",
  "isMulti",
  "isSearchable",
  "onMenuOpen",
  "onMenuClose",
  "defaultValue",
  "openMenuOnClick",
  "options",
  "placeholder",
  "value",
  "noOptionsMessage",
  "menuPlacement",
  "menuPosition",
  "maxMenuHeight",
  "onInputChange",
  "isLoading",
  "onMenuScrollToTop",
  "onMenuScrollToBottom",
  "isOptionDisabled"
];

export const COMMON_ASYNC_PROPS = [
  "isLoading",
  "onInputChange",
  "defaultOptions",
  "loadOptions",
  "cacheOptions",
  "debounceDelay"
];

export default class SelectUtils {
  getPropsForType = (props, type) => {
    if (type === "select") {
      return this.getPropsForSelect(props);
    }
    if (type === "creatable") {
      return this.getPropsForCreatableSelect(props);
    }
    if (type === "async") {
      return this.getPropsForAsyncSelect(props);
    }
    if (type === "async-creatable") {
      return this.getPropsForAsyncCreatableSelect(props);
    }

    return null;
  };

  getPropsForSelect = props => {
    return gatherConditionalProps(props, ...COMMON_PROPS);
  };

  getPropsForCreatableSelect = props => {
    return gatherConditionalProps(
      props,
      ...COMMON_PROPS,
      "onCreateOption",
      "formatCreateLabel"
    );
  };

  getPropsForAsyncSelect(props) {
    const asyncProps = gatherConditionalProps(
      props,
      ...COMMON_PROPS,
      ...COMMON_ASYNC_PROPS
    );
    asyncProps.loadOptions = this.getLoadOptions(asyncProps);

    return asyncProps;
  }

  getPropsForAsyncCreatableSelect(props) {
    const asyncProps = gatherConditionalProps(
      props,
      ...COMMON_PROPS,
      ...COMMON_ASYNC_PROPS,
      "onCreateOption",
      "formatCreateLabel"
    );
    asyncProps.loadOptions = this.getLoadOptions(asyncProps);

    return asyncProps;
  }

  getLoadOptions = ({ loadOptions, debounceDelay }) => {
    const delayInt = parseInt(debounceDelay, 10);
    // eslint-disable-next-line no-restricted-globals
    const delay = !isNaN(delayInt) && delayInt >= 0 ? debounceDelay : 0;

    if (typeof loadOptions === "function") {
      return debounce(loadOptions, delay);
    }
    return undefined;
  };

  getFilterOption = (props, type) => {
    if (type === "async" || type === "async-creatable") {
      return {
        filterOption: options => options
      };
    }

    if (props.filterConfig && Object.keys(props.filterConfig).length > 0) {
      return {
        filterOption: createFilter(props.filterConfig)
      };
    }

    return {
      filterOption: createFilter({
        ignoreCase: true,
        ignoreAccents: true,
        trim: true,
        matchFrom: "any",
        stringify: option => `${option.label}`
      })
    };
  };

  getNoOptionsMessage = ({ noOptionsMessage }, type) => {
    if (type === "async" || type === "async-creatable") {
      if (noOptionsMessage && isFunction(noOptionsMessage)) {
        return {
          noOptionsMessage: inputProps => {
            if (!inputProps.inputValue.trim()) {
              return null;
            }
            return noOptionsMessage(inputProps);
          }
        };
      }

      return {
        noOptionsMessage: ({ inputValue }) =>
          inputValue.trim() ? "No options" : null
      };
    }
    return noOptionsMessage || {};
  };

  getMenuPortalTarget = props => {
    if (props.menuPosition === "fixed") {
      return { menuPortalTarget: document.body };
    }
    return {};
  };

  getOverriddenStyles = (fn, state) => {
    if (isFunction(fn)) {
      return fn.call(null, state);
    }

    return {};
  };

  getRestStyles = rest => {
    const styleKeys = Object.keys(rest);
    const restNewStyles = {};

    styleKeys.forEach(styleKey => {
      const styleFn = rest[styleKey];
      restNewStyles[styleKey] = (base, state) => ({
        ...base,
        ...(styleFn(state) || {})
      });
    });

    return restNewStyles;
  };

  getSelectStyles = styles => {
    const targetHeight = 32;
    const {
      placeholder = null,
      singleValue = null,
      control = null,
      valueContainer = null,
      clearIndicator = null,
      dropdownIndicator = null,
      menuPortal = null,
      multiValue = null,
      multiValueLabel = null,
      multiValueRemove = null,
      ...restStyles
    } = styles || {};

    return {
      placeholder: (base, state) => {
        return {
          ...base,
          color: "#adb5bd",
          ...this.getOverriddenStyles(placeholder, state)
        };
      },
      singleValue: (base, state) => {
        return {
          ...base,
          color: "#495057",
          ...this.getOverriddenStyles(singleValue, state)
        };
      },
      control: (base, state) => ({
        ...base,
        minHeight: "initial",
        ...this.getOverriddenStyles(control, state)
      }),
      valueContainer: (base, state) => ({
        ...base,
        minHeight: `${targetHeight - 1 - 1}px`,
        padding: "0 8px",
        ...this.getOverriddenStyles(valueContainer, state)
      }),
      clearIndicator: (base, state) => ({
        ...base,
        padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
        ...this.getOverriddenStyles(clearIndicator, state)
      }),
      dropdownIndicator: (base, state) => ({
        ...base,
        padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
        ...this.getOverriddenStyles(dropdownIndicator, state)
      }),
      menuPortal: (base, state) => ({
        ...base,
        zIndex: 1070,
        ...this.getOverriddenStyles(menuPortal, state)
      }),
      multiValue: (base, state) => ({
        ...base,
        backgroundColor: "#e9ecef",
        color: state.isDisabled ? "#6c757d" : base.color,
        ...this.getOverriddenStyles(multiValue, state)
      }),
      multiValueLabel: (base, state) => ({
        ...base,
        fontSize: "11px",
        color: "inherit",
        ...this.getOverriddenStyles(multiValueLabel, state)
      }),
      multiValueRemove: (base, state) => ({
        ...base,
        color: "#737373",
        ":hover": {
          color: "#000",
          backgroundColor: "inherit"
        },
        ...this.getOverriddenStyles(multiValueRemove, state)
      }),

      ...(this.getRestStyles(restStyles) || {})
    };
  };
}
